import {
  Box,
  Checkbox,
  Chip,
  InputAdornment,
  Link,
  ListItemIcon,
  ListItemText,
  ListSubheader,
  MenuItem,
  MenuList,
  Popover,
  TextField,
  Typography,
} from "@mui/material";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import CheckIcon from "@mui/icons-material/Check";
import SearchIcon from "@mui/icons-material/Search";
import { ReactNode, useMemo, useRef, useState } from "react";

interface FilterChipRowProps {
  children: ReactNode;
  /** When given, a "Clear filters" link is shown that calls it. */
  onClear?: () => void;
  testId?: string;
  clearTestId?: string;
}

// The "Filter by" row that holds a page's filter chips, with an optional
// clear-all link. Pages pass onClear only while a filter is active.
export const FilterChipRow = ({ children, onClear, testId, clearTestId }: FilterChipRowProps) => (
  <Box
    sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap", mt: 1.5, pl: 1 }}
    data-testid={testId}
  >
    <Typography variant="body2" color="text.secondary" sx={{ mr: 0.5 }}>
      Filter by
    </Typography>
    {children}
    {onClear && (
      <Link
        component="button"
        type="button"
        variant="body2"
        underline="always"
        color="primary"
        onClick={onClear}
        sx={{ cursor: "pointer", ml: 0.5 }}
        data-testid={clearTestId}
      >
        Clear filters
      </Link>
    )}
  </Box>
);

export interface PickerOption {
  value: string;
  label: string;
  /** Optional swatch shown before the label, e.g. an event type colour. */
  color?: string;
  /** Optional heading the option is listed under when the list is not being searched. */
  group?: string;
}

interface PickerProps {
  name: string;
  open: boolean;
  anchorEl: HTMLElement | null;
  onClose: () => void;
  options: PickerOption[];
  searchable?: boolean;
  searchPlaceholder?: string;
  /** Called when the user presses Enter with exactly one match. */
  onEnterSingleMatch?: (option: PickerOption) => void;
  /** The "All" / "Any" row at the top of the list. */
  anyRow: ReactNode;
  renderOption: (option: PickerOption) => ReactNode;
}

const Swatch = ({ color }: { color?: string }) =>
  color ? (
    <Box
      component="span"
      sx={{
        display: "inline-block",
        width: 10,
        height: 10,
        borderRadius: "50%",
        bgcolor: color,
        mr: 1,
        flexShrink: 0,
      }}
    />
  ) : null;

/** Label with an optional colour swatch, for use inside a ListItemText. */
export const OptionLabel = ({ option }: { option: PickerOption }) => (
  <Box component="span" sx={{ display: "inline-flex", alignItems: "center" }}>
    <Swatch color={option.color} />
    {option.label}
  </Box>
);

// The popover shared by the single- and multi-select chips: optional search
// box, the "any" row, then the options, grouped under subheaders when the
// options carry a group and the list isn't being searched.
const Picker = (props: PickerProps) => {
  const { name, open, anchorEl, onClose, options, searchable, searchPlaceholder, onEnterSingleMatch, anyRow, renderOption } = props;
  const [query, setQuery] = useState("");

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return q ? options.filter((o) => o.label.toLowerCase().includes(q)) : options;
  }, [options, query]);

  const grouped = !query && options.some((o) => o.group);
  const groups = useMemo(() => {
    if (!grouped) return [];
    const order: string[] = [];
    const byGroup = new Map<string, PickerOption[]>();
    for (const o of filtered) {
      const g = o.group || "";
      if (!byGroup.has(g)) {
        order.push(g);
        byGroup.set(g, []);
      }
      byGroup.get(g)!.push(o);
    }
    return order.map((g) => ({ name: g, options: byGroup.get(g)! }));
  }, [grouped, filtered]);

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      onTransitionExited={() => setQuery("")}
      anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      transformOrigin={{ vertical: "top", horizontal: "left" }}
      slotProps={{ paper: { sx: { width: 300, mt: 0.5 } } }}
    >
      {searchable && (
        <Box sx={{ p: 1 }}>
          <TextField
            size="small"
            fullWidth
            autoFocus
            placeholder={searchPlaceholder || "Search..."}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && filtered.length === 1 && onEnterSingleMatch) {
                onEnterSingleMatch(filtered[0]);
              }
            }}
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              },
              htmlInput: { "aria-label": `Search ${name.toLowerCase()}s` },
            }}
          />
        </Box>
      )}
      <MenuList dense sx={{ maxHeight: 320, overflowY: "auto", pt: searchable ? 0 : 1 }}>
        {!query && anyRow}
        {grouped
          ? groups.map((g) => [
              <ListSubheader key={`h-${g.name}`} disableSticky sx={{ lineHeight: "28px", bgcolor: "transparent" }}>
                {g.name}
              </ListSubheader>,
              ...g.options.map(renderOption),
            ])
          : filtered.map(renderOption)}
        {filtered.length === 0 && (
          <MenuItem disabled>
            <ListItemText>No matches</ListItemText>
          </MenuItem>
        )}
      </MenuList>
    </Popover>
  );
};

interface ChipShellProps {
  label: string;
  active: boolean;
  open: boolean;
  onOpen: () => void;
  onClear: () => void;
  testId: string;
}

// Inactive chips show a caret; active chips fill in the primary colour and
// show an X that clears just that filter.
const ChipShell = ({ label, active, open, onOpen, onClear, testId, chipRef }: ChipShellProps & { chipRef: React.RefObject<HTMLDivElement | null> }) => (
  <Chip
    ref={chipRef}
    label={label}
    onClick={onOpen}
    onDelete={active ? onClear : onOpen}
    deleteIcon={active ? undefined : <ArrowDropDownIcon />}
    color={active ? "primary" : "default"}
    variant={active ? "filled" : "outlined"}
    aria-haspopup="listbox"
    aria-expanded={open}
    data-testid={testId}
  />
);

export interface FilterChipProps {
  name: string; // e.g. "Stock" - shown as the chip prefix
  anyLabel: string; // label for the "no filter" choice, e.g. "All" or "Any"
  value: string | null; // null = no filter
  options: PickerOption[];
  onChange: (value: string | null) => void;
  searchable?: boolean;
  searchPlaceholder?: string;
  testId: string;
}

// A chip that names its filter and current value ("Supplier: Any") and opens
// a single-choice picker when clicked. Choosing closes the picker.
export const FilterChip = (props: FilterChipProps) => {
  const { name, anyLabel, value, options, onChange, searchable, searchPlaceholder, testId } = props;
  const chipRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);

  const active = value !== null;
  const selected = options.find((o) => o.value === value);
  // Fall back to the raw value so a persisted choice that is no longer in
  // the option list (e.g. a renamed location) is still visible and clearable.
  const valueLabel = selected ? selected.label : active ? value : anyLabel;

  const close = () => setOpen(false);
  const choose = (next: string | null) => {
    onChange(next);
    close();
  };

  return (
    <>
      <ChipShell
        chipRef={chipRef}
        label={`${name}: ${valueLabel}`}
        active={active}
        open={open}
        onOpen={() => setOpen(true)}
        onClear={() => onChange(null)}
        testId={testId}
      />
      <Picker
        name={name}
        open={open}
        anchorEl={chipRef.current}
        onClose={close}
        options={options}
        searchable={searchable}
        searchPlaceholder={searchPlaceholder}
        onEnterSingleMatch={(o) => choose(o.value)}
        anyRow={
          <MenuItem selected={!active} onClick={() => choose(null)}>
            <ListItemIcon>{!active && <CheckIcon fontSize="small" />}</ListItemIcon>
            <ListItemText>{anyLabel}</ListItemText>
          </MenuItem>
        }
        renderOption={(o) => (
          <MenuItem key={o.value} selected={o.value === value} onClick={() => choose(o.value)}>
            <ListItemIcon>{o.value === value && <CheckIcon fontSize="small" />}</ListItemIcon>
            <ListItemText>
              <OptionLabel option={o} />
            </ListItemText>
          </MenuItem>
        )}
      />
    </>
  );
};

export interface MultiFilterChipProps {
  name: string;
  anyLabel: string;
  value: string[]; // empty = no filter
  options: PickerOption[];
  onChange: (value: string[]) => void;
  searchable?: boolean;
  searchPlaceholder?: string;
  testId: string;
}

// The multi-choice version: each row has a checkbox and toggling keeps the
// picker open. The "All" row clears the selection. The chip shows the chosen
// names when there are one or two, otherwise a count.
export const MultiFilterChip = (props: MultiFilterChipProps) => {
  const { name, anyLabel, value, options, onChange, searchable, searchPlaceholder, testId } = props;
  const chipRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);

  const active = value.length > 0;
  const labelFor = (v: string) => options.find((o) => o.value === v)?.label ?? v;
  const valueLabel = !active
    ? anyLabel
    : value.length <= 2
      ? value.map(labelFor).join(", ")
      : `${value.length} selected`;

  const toggle = (v: string) =>
    onChange(value.includes(v) ? value.filter((x) => x !== v) : [...value, v]);

  return (
    <>
      <ChipShell
        chipRef={chipRef}
        label={`${name}: ${valueLabel}`}
        active={active}
        open={open}
        onOpen={() => setOpen(true)}
        onClear={() => onChange([])}
        testId={testId}
      />
      <Picker
        name={name}
        open={open}
        anchorEl={chipRef.current}
        onClose={() => setOpen(false)}
        options={options}
        searchable={searchable}
        searchPlaceholder={searchPlaceholder}
        onEnterSingleMatch={(o) => toggle(o.value)}
        anyRow={
          <MenuItem selected={!active} onClick={() => onChange([])}>
            <ListItemIcon>{!active && <CheckIcon fontSize="small" />}</ListItemIcon>
            <ListItemText>{anyLabel}</ListItemText>
          </MenuItem>
        }
        renderOption={(o) => {
          const checked = value.includes(o.value);
          return (
            <MenuItem key={o.value} onClick={() => toggle(o.value)} aria-checked={checked} role="menuitemcheckbox">
              <ListItemIcon>
                <Checkbox size="small" checked={checked} disableRipple tabIndex={-1} sx={{ p: 0 }} />
              </ListItemIcon>
              <ListItemText>
                <OptionLabel option={o} />
              </ListItemText>
            </MenuItem>
          );
        }}
      />
    </>
  );
};
